import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { AccountService, WebSocketService } from '@core/services'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { faPaperPlane } from '@fortawesome/free-solid-svg-icons'
import { faArrowsUpDownLeftRight } from '@fortawesome/free-solid-svg-icons'
import { RushGameHttp } from '@rush-core/https'
import { LiveMessageTypeEnum } from '@rush-shared/enums'
import { LiveMessageModel } from '@rush-shared/models'
import { DebounceClickDirective } from '@shared/directives'

enum LiveMessageModeEnum {
  Collapse = 1,
  Disintegrate = 2,
  Shrank = 3,
  Expanded = 4,
}

@Component({
  selector: 'app-live-messages',
  standalone: true,
  imports: [FontAwesomeModule, FormsModule, CommonModule, DebounceClickDirective],
  templateUrl: './live-messages.component.html',
  styleUrl: './live-messages.component.sass',
  encapsulation: ViewEncapsulation.None,
})
export class LiveMessagesComponent implements OnInit, AfterViewInit {
  @Input() messages: LiveMessageModel[] = []
  @Input() isAdmin = false
  @Input() type!: LiveMessageTypeEnum

  @ViewChild('liveMessages') liveMessages: ElementRef
  @ViewChild('draggableButton') draggableButton: ElementRef

  newMessageArrive = false
  readonly faPaperPlane = faPaperPlane
  readonly faArrowsUpDownLeftRight = faArrowsUpDownLeftRight
  composedMessage = ''
  mode: LiveMessageModeEnum = LiveMessageModeEnum.Collapse

  draggableActivated = false
  oldCursorX = 0
  oldCursorY = 0
  expanderHoldTimerHandler: any
  haveCollapse = false // preventing collapse animation to trigger on initial load

  get isCollapse() {
    return this.mode === LiveMessageModeEnum.Collapse && this.haveCollapse
  }
  get isDisintegrate() {
    return this.mode === LiveMessageModeEnum.Disintegrate
  }
  get isShrank() {
    return this.mode === LiveMessageModeEnum.Shrank
  }
  get isExpanded() {
    return this.mode === LiveMessageModeEnum.Expanded
  }

  constructor(
    private _accountService: AccountService,
    private _webSocketService: WebSocketService,
    private _rushGameHttp: RushGameHttp,
  ) {}

  ngOnInit(): void {
    this._webSocketService.listenToLiveMessages(this.type, (messages) => {
      this.messages = messages
      this.newMessageArrive = true
    })
  }

  ngAfterViewInit(): void {
    document.addEventListener('mousemove', (e) => {
      this.onDragMove(e.clientX, e.clientY)
    })

    document.addEventListener('touchmove', (e) => {
      this.onDragMove(e.touches[0].clientX, e.touches[0].clientY)
    })
  }

  sendMessage() {
    if (this.composedMessage !== '') {
      const message = {
        sender: this._accountService.userInitData?.accountName || '',
        message: this.composedMessage,
        fromAdmin: this.isAdmin,
      }

      this._rushGameHttp.sendLiveMessage(this.type, message).subscribe({
        error: (error) => {
          console.error(error)
        },
      })

      this.composedMessage = ''
    }
  }

  onDragMove(xAxis: number, yAxis: number) {
    if (!this.draggableActivated) {
      return
    }

    const currentLeft = Number(this.liveMessages.nativeElement.style.left.replace('px', ''))
    const currentTop = Number(this.liveMessages.nativeElement.style.top.replace('px', ''))

    this.liveMessages.nativeElement.style.top = currentTop + (yAxis - this.oldCursorY) + 'px'
    this.liveMessages.nativeElement.style.left = currentLeft + (xAxis - this.oldCursorX) + 'px'

    this.oldCursorX = xAxis
    this.oldCursorY = yAxis
  }

  activateDraggable(e: MouseEvent) {
    this.draggableActivated = true
    this.oldCursorX = e.clientX
    this.oldCursorY = e.clientY
  }

  touchActivateDraggable(e: TouchEvent) {
    this.draggableActivated = true
    this.oldCursorX = e.touches[0].clientX
    this.oldCursorY = e.touches[0].clientY
  }

  deactivateDraggable() {
    this.draggableActivated = false
  }

  onUnholdExpander() {
    if (this.expanderHoldTimerHandler) {
      clearTimeout(this.expanderHoldTimerHandler)
      this.expanderHoldTimerHandler = 0
    }
  }

  onChevronClick() {
    this.haveCollapse = true
    this.modeSwitcher()
  }

  onHoldExpander() {
    this.expanderHoldTimerHandler = setTimeout(() => {
      this.expanderHoldTimerHandler = 0

      if (this.isDisintegrate || this.isShrank) {
        this.mode = LiveMessageModeEnum.Collapse
      }
    }, 500)
  }

  private modeSwitcher() {
    switch (this.mode) {
      case LiveMessageModeEnum.Collapse:
      case LiveMessageModeEnum.Disintegrate:
      case LiveMessageModeEnum.Shrank:
        this.mode = LiveMessageModeEnum.Expanded
        break
      case LiveMessageModeEnum.Expanded:
        this.mode = LiveMessageModeEnum.Collapse
        break

      default:
        break
    }
  }
}
